import {Method} from "axios";
import DataResponse from "@/models/data-response";
import {headersJson, endpointsJson} from "@/configs"
import {IEndpoints, IHeaderRequest, State} from "@/types/api-config";
import {makeAutoObservable} from "mobx";
import {getFPrint} from "@/wasm/finger-print/finger_print";
import {IEndpoint} from "@/types/api-config";
import {Request} from "@/api/request";
import Session from "@/models/session";
import {IRole} from "@/types/auth-data";
import ErrorResponse from "@/models/error-response";
import DataRequest from "@/models/data-request";

export interface IParamRequest<T extends DataRequest> {
    endpoint: Endpoint | null;
    data?: T,
    isSetSession?: boolean
}


export class ApiConfig {
    baseURL: string = process.env.baseUrl;
    headers: HeaderRequest = new HeaderRequest(headersJson as IHeaderRequest);
    allowedRolesToAuth: IRole[] = [IRole.Admin, IRole.User];
    endpoints: Endpoints = new Endpoints(endpointsJson as IEndpoints);
    session = new Session();
    isAuth: boolean = false;
    errorResponse: ErrorResponse | null = null;
    info: string | null = "";
    private _stateUpdateToken: Promise<void> | null = null;

    constructor() {
        makeAutoObservable(this)
    }

    request = <Rq extends DataRequest>(paramRequest: IParamRequest<Rq>): Promise<void> => {
        const {endpoint, data, isSetSession} = paramRequest;
        if (!endpoint) {
            throw new Error(`Неверная кофигурация API, postRegData не существует`)
        }
        if (data) {
            endpoint.dataRequest = data.toJson();
        }
        const setErrorResponse = this.setErrorResponse;
        const setState = endpoint.setState;
        const setDataResponse = endpoint.setDataResponse
        setState("loading");
        return this.updateToken(endpoint).then(request =>
            request.send().then(response => {
                if (response instanceof DataResponse) {
                    isSetSession && this.setSession(response)
                    setDataResponse(response)
                    setState("success");
                }
                if (response instanceof ErrorResponse) {
                    setErrorResponse(response);
                    setState("error");
                }
            }).catch(error => {
                console.error(`API Request ${endpoint.url} Failed:, ${error}`)
            }))
    }
    setErrorResponse = (errorResponse: ErrorResponse | null): void => {
        this.errorResponse = errorResponse
    }

    setInfo = (info: string | null): void => {
        this.info = info
    }

    private setXToken = (value: string | null) => {
        if (value) {
            localStorage.setItem("x-token", value);
            this.headers.xToken = value;
        } else {
            localStorage.removeItem("x-token");
            this.headers.xToken = null;
        }
    }
    private updateToken(endpoint: Endpoint): Promise<Request> {
        const updateToken = async (endpoint: Endpoint): Promise<void> | null => {
            const endpointUpdateToken = this.endpoints.updateToken;
            const notAuth = !endpoint.isProtected;
            const expSession = this.session.exp * 1000;
            const currentTime = Date.now();
            const bufferTime = 15000;
            if (notAuth || !expSession || expSession > currentTime + bufferTime) return;
            if (this._stateUpdateToken) return this._stateUpdateToken;

            this._stateUpdateToken = (async () => {
                const response = await new Request(this, endpointUpdateToken).send();
                response instanceof DataResponse && this.setSession(response);
            })();

            try {
                await this._stateUpdateToken;
            } finally {
                this._stateUpdateToken = null;
            }
        }

        const request = async () => {
            await updateToken(endpoint);
            return new Request(this, endpoint)
        }
        return request()
    }
    private setIsAuth = (isAuth: boolean): void => {
        this.isAuth = isAuth
    }
    private setSession = (response: DataResponse) => {
        const xToken = response.headers?.['x-token'] || null;
        this.setXToken(xToken);

        const sessionData = response.headers?.['session'] || null;
        this.session.setItemSession(sessionData);

        const userRole = this.session.role;
        const isAllowed = userRole && this.allowedRolesToAuth.includes(userRole as IRole);
        this.setIsAuth(!!isAllowed);
    }
}

export class HeaderRequest {
    accept: string;
    contentType: string;
    private _fPrt?: string | null = null;
    private _xToken?: string | null = null;

    constructor(headers: IHeaderRequest) {
        this._fPrt = getFPrint();
        this._xToken = localStorage.getItem("x-token") || null;
        this.accept = headers.accept;
        this.contentType = headers.contentType;
    }

    set xToken(value: string | null) {
        this._xToken = value;
    }

    get xToken(): string | null {
        return this._xToken;
    }

    set fPrt(value: string | null) {
        this._fPrt = value;
    }

    toObject(): { [key: string]: any } {
        return Object.entries(this)
            .reduce<{ [key: string]: any }>((accumulator, [key, value]) => {
                const newKey = key.replace(/([a-z])([A-Z])/g, '$1-$2')
                    .toLowerCase().replace(/^_/, '');
                accumulator[newKey] = value;
                return accumulator;
            }, {});
    }
}

export class Endpoints {
    [key: string]: Endpoint;

    constructor(endpoints: IEndpoints) {
        for (const key in endpoints) {
            if (endpoints.hasOwnProperty(key)) {
                this[key] = new Endpoint(endpoints[key]);
            }
        }
    }
}

export class Endpoint {
    method: Method;
    isProtected: boolean;
    url: string;
    dataRequest?: any;
    state: State;
    dataResponse: DataResponse | null = null;

    constructor(endpoint: IEndpoint) {
        makeAutoObservable(this)
        this.method = endpoint.method;
        this.isProtected = endpoint.isProtected;
        this.url = endpoint.url;
        this.state = null;
        this.dataResponse = null;
    }

    setDataResponse = (dataResponse: DataResponse): void => {
        this.dataResponse = dataResponse;
    }

    setState = (state: State): void => {
        this.state = state;
    }
}

